import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import type { Job } from '@tipstrack/shared';
import {
  expireJob,
  getJob,
  listApplications,
  publishJob,
  updateJob,
  type JobInput,
} from '../api/endpoints.js';
import { StageBadge } from '../components/StageBadge.js';
import { SyncStatusPill } from '../components/SyncStatusPill.js';
import { errorMessage } from '../api/client.js';
import { fmtDateTime, fmtSalary } from '../lib/format.js';

function jobToForm(job: Job): JobInput {
  return {
    title: job.title,
    descriptionHtml: job.descriptionHtml,
    country: job.country,
    cityRegionPostal: job.cityRegionPostal,
    contactEmail: job.contactEmail,
    contactPhone: job.contactPhone ?? '',
    applyUrl: job.applyUrl ?? '',
    salaryCurrency: job.salaryCurrency ?? '',
    salaryPeriod: job.salaryPeriod ?? '',
    salaryMinMinor: job.salaryMinMinor,
    benefits: job.benefits,
  };
}

export function JobDetailPage() {
  const { jobId = '' } = useParams();
  const queryClient = useQueryClient();

  const jobQuery = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => getJob(jobId),
    enabled: Boolean(jobId),
  });
  const applicationsQuery = useQuery({
    queryKey: ['applications', { jobId }],
    queryFn: () => listApplications({ jobId }),
    enabled: Boolean(jobId),
  });

  const [form, setForm] = useState<JobInput | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // (Re-)seed the edit form whenever fresh job data lands.
  useEffect(() => {
    if (jobQuery.data) setForm(jobToForm(jobQuery.data));
  }, [jobQuery.data]);

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['job', jobId] });
    void queryClient.invalidateQueries({ queryKey: ['jobs'] });
  }

  const saveMutation = useMutation({
    mutationFn: (input: JobInput) =>
      updateJob(jobId, {
        ...input,
        contactPhone: input.contactPhone || null,
        applyUrl: input.applyUrl || null,
        salaryCurrency: input.salaryCurrency || null,
        salaryPeriod: input.salaryPeriod || null,
      }),
    onSuccess: () => {
      invalidate();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
    onError: (err) => setActionError(errorMessage(err)),
  });

  const publishMutation = useMutation({
    mutationFn: () => publishJob(jobId),
    onSuccess: invalidate,
    onError: (err) => setActionError(errorMessage(err)),
  });

  const expireMutation = useMutation({
    mutationFn: () => expireJob(jobId),
    onSuccess: invalidate,
    onError: (err) => setActionError(errorMessage(err)),
  });

  function set<K extends keyof JobInput>(key: K, value: JobInput[K]) {
    setForm((f) => (f ? { ...f, [key]: value } : f));
  }

  function onSave(e: FormEvent) {
    e.preventDefault();
    setActionError(null);
    if (form) saveMutation.mutate(form);
  }

  if (jobQuery.isLoading) {
    return (
      <div className="route-center">
        <div className="spinner" aria-label="Loading job" />
      </div>
    );
  }
  if (jobQuery.isError || !jobQuery.data) {
    return <div className="alert alert--error">Failed to load job.</div>;
  }

  const job = jobQuery.data;
  const applications = applicationsQuery.data ?? [];
  const salary = fmtSalary(job.salaryCurrency, job.salaryPeriod, job.salaryMinMinor);

  return (
    <div className="page">
      <header className="page__header">
        <div>
          <div className="breadcrumb">
            <Link to="/jobs">Jobs</Link> / {job.title}
          </div>
          <h1>{job.title}</h1>
          <div className="kv-row">
            <span className={`pill job-${job.status}`}>{job.status}</span>
            <SyncStatusPill status={job.indeedSyncStatus} />
            {salary && <span className="muted">{salary}</span>}
            {job.indeedLastSyncedAt && (
              <span className="muted">Last synced {fmtDateTime(job.indeedLastSyncedAt)}</span>
            )}
          </div>
        </div>
        <div className="page__actions">
          <button
            type="button"
            className="btn btn--primary"
            disabled={publishMutation.isPending}
            onClick={() => {
              if (window.confirm('Publish this job to Indeed?')) {
                setActionError(null);
                publishMutation.mutate();
              }
            }}
          >
            {publishMutation.isPending ? 'Publishing…' : 'Publish to Indeed'}
          </button>
          <button
            type="button"
            className="btn btn--danger"
            disabled={expireMutation.isPending}
            onClick={() => {
              if (window.confirm('Expire this job on Indeed? Applicants will no longer see it.')) {
                setActionError(null);
                expireMutation.mutate();
              }
            }}
          >
            {expireMutation.isPending ? 'Expiring…' : 'Expire'}
          </button>
        </div>
      </header>

      {actionError && <div className="alert alert--error">{actionError}</div>}
      {job.indeedSyncError && (
        <div className="alert alert--error">Indeed sync error: {job.indeedSyncError}</div>
      )}
      {saved && <div className="alert alert--success">Job saved.</div>}

      {form && (
        <section className="card">
          <h2 className="card__title">Edit job</h2>
          <form onSubmit={onSave} className="form-grid">
            <label className="field field--full">
              <span className="field__label">Title</span>
              <input value={form.title} onChange={(e) => set('title', e.target.value)} required />
            </label>
            <label className="field">
              <span className="field__label">City / region / postal</span>
              <input
                value={form.cityRegionPostal}
                onChange={(e) => set('cityRegionPostal', e.target.value)}
                required
              />
            </label>
            <label className="field">
              <span className="field__label">Country</span>
              <input
                value={form.country}
                onChange={(e) => set('country', e.target.value)}
                required
                maxLength={2}
              />
            </label>
            <label className="field">
              <span className="field__label">Contact email</span>
              <input
                type="email"
                value={form.contactEmail}
                onChange={(e) => set('contactEmail', e.target.value)}
                required
              />
            </label>
            <label className="field">
              <span className="field__label">Contact phone</span>
              <input
                value={form.contactPhone ?? ''}
                onChange={(e) => set('contactPhone', e.target.value)}
              />
            </label>
            <label className="field">
              <span className="field__label">Salary currency</span>
              <input
                value={form.salaryCurrency ?? ''}
                onChange={(e) => set('salaryCurrency', e.target.value)}
                placeholder="USD"
                maxLength={3}
              />
            </label>
            <label className="field">
              <span className="field__label">Salary period</span>
              <select
                value={form.salaryPeriod ?? ''}
                onChange={(e) => set('salaryPeriod', e.target.value)}
              >
                <option value="">—</option>
                <option value="YEAR">Year</option>
                <option value="MONTH">Month</option>
                <option value="HOUR">Hour</option>
              </select>
            </label>
            <label className="field">
              <span className="field__label">Salary minimum (minor units, e.g. cents)</span>
              <input
                type="number"
                min={0}
                value={form.salaryMinMinor ?? ''}
                onChange={(e) =>
                  set('salaryMinMinor', e.target.value === '' ? null : Number(e.target.value))
                }
              />
            </label>
            <label className="field field--full">
              <span className="field__label">Description (HTML)</span>
              <textarea
                value={form.descriptionHtml}
                onChange={(e) => set('descriptionHtml', e.target.value)}
                rows={10}
                required
              />
            </label>
            <div className="modal__actions">
              <button type="submit" className="btn btn--primary" disabled={saveMutation.isPending}>
                {saveMutation.isPending ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </form>
        </section>
      )}

      <section className="card">
        <h2 className="card__title">Applications ({applications.length})</h2>
        {applications.length === 0 ? (
          <p className="muted">No applications for this job yet.</p>
        ) : (
          <table className="table table--hover">
            <thead>
              <tr>
                <th>Candidate</th>
                <th>Stage</th>
                <th>Applied</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {applications.map((a) => (
                <tr key={a.id}>
                  <td>
                    <Link to={`/applications/${a.id}`}>
                      {a.candidate?.fullName ?? 'Unknown candidate'}
                    </Link>
                  </td>
                  <td>
                    <StageBadge stageKey={a.stageKey} />
                  </td>
                  <td>{fmtDateTime(a.appliedAt)}</td>
                  <td>{a.indeedApplyId ? 'Indeed' : 'Manual'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
